import React, { useReducer, useCallback, useContext } from 'react'
import CardTransition from './components/CardTransition'

interface ICardTransitionContext {
  openCard: ({ Component, title }: { Component: React.ReactNode; title?: string }) => void
  closeCard: () => void
}

export const CardTransitionContext = React.createContext<ICardTransitionContext | null>(null)

const { Provider } = CardTransitionContext

enum Actions {
  OPEN_CARD = 'CT/OPEN_CARD',
  CLOSE_CARD = 'CT/CLOSE_CARD',
}

interface IState {
  shown: boolean
  Component?: React.ReactNode
  title?: string
}

interface IAction {
  type: Actions
  payload?: {
    Component?: React.ReactNode
    title?: string
  }
}

const reducer = (prevState: IState, { type, payload }: IAction) => {
  switch (type) {
    case Actions.OPEN_CARD:
      return {
        ...prevState,
        Component: payload?.Component,
        title: payload?.title,
        shown: true,
      }

    case Actions.CLOSE_CARD:
      return {
        ...prevState,
        Component: null,
        title: undefined,
        shown: false,
      }
    default:
      return prevState
  }
}

interface IProps {
  children: React.ReactNode
}

export const CardTransitionProvider = ({ children }: IProps) => {
  const [{ shown, Component, title }, dispatch] = useReducer(reducer, { shown: false })

  const openCard = useCallback(({ Component, title }: { Component: React.ReactNode; title?: string }) => {
    dispatch({ type: Actions.OPEN_CARD, payload: { Component, title } })
  }, [])

  const closeCard = useCallback(() => {
    dispatch({ type: Actions.CLOSE_CARD })
  }, [])

  return (
    <Provider
      value={{
        openCard,
        closeCard,
      }}
    >
      {children}
      <CardTransition shown={shown} title={title}>
        {Component}
      </CardTransition>
    </Provider>
  )
}

export const useCardTransition = () => {
  const ctx = useContext(CardTransitionContext)
  if (!ctx) {
    throw Error('The `useCardTransition` hook must be called from a descendent of the `CardTransitionProvider`.')
  }

  return ctx
}
